import { methods } from './constants';

import {
  daySelectionOptions,
  monthDaysOptions,
  timeOptions,
  timeZoneOptions,
} from '../config/constants';

// eslint-disable-next-line no-shadow
export enum TriggerType {
  Schedule = 'Schedule',
  'Webhook API' = 'Webhook API',
  Assistants = 'Assistants',
  'Send HTTP Request' = 'Send HTTP Request',
  API = 'API',
  Gateways = 'Gateways',
  'Send Prompt' = 'Send Prompt',
  'send prompt' = 'send prompt',
}

export interface Fields {
  element:
    | 'input'
    | 'textarea'
    | 'slider'
    | 'select'
    | 'searchSelect'
    | 'triggerType'
    | 'toggle'
    | 'keyValuePair';
  props: {
    name?: string;
    label?: string;
    required?: boolean;
    valueLabelDisplay?: string;
    step?: number;
    editable?: boolean;
    profileInputStyle?: boolean;
    min?: number;
    max?: number;
    type?: string;
    placeholder?: string;
    rows?: number;
    withKeyLabels?: boolean;
    isRequired?: boolean;
    isAddRemoveDynamicOptions?: boolean;
    isDynamicOptionsModel?: boolean;
    isWithMessage?: boolean;
  };
  options?: Array<{ id: string; label: string; value: string }>;
  dependency?: Array<{
    inputName: string;
    value: string | number;
  }>;
  keyValuePairs?: { item?: string; key?: string; value?: string }[];
}

export interface Configurations {
  setup: Array<Fields>;
  configuration: Array<Fields>;
  test: string;
}

export type WorkflowFields = {
  [key in TriggerType]: Configurations;
};

const baseSetupConfig: Array<Fields> = [
  {
    element: 'triggerType',
    props: {
      label: 'triggerType',
      name: 'triggerType',
      required: true,
    },
  },
];
let WorkFlowData: WorkflowFields = {
  Schedule: {
    setup: [
      ...baseSetupConfig,
      {
        element: 'searchSelect',
        props: {
          label: 'Trigger Events',
          name: 'Schedule', // name change with Schedule
          required: true,
        },
      },
    ],
    configuration: [
      {
        element: 'slider',
        props: {
          label: 'Interval range',
          name: 'interval_range',
          valueLabelDisplay: 'auto',
          // min: 0,
          // max: 1,
          step: 0.01,
          // required: true
        },
        dependency: [
          {
            inputName: 'Schedule',
            value: 'interval_range',
          },
        ],
      },
      {
        element: 'searchSelect',
        props: {
          label: 'Select a day of the month',
          name: 'day_of_month_dropdown',
          // min: 0,
          // max: 1,
          step: 0.01,
          // required: true
        },
        options: monthDaysOptions,
        dependency: [
          {
            inputName: 'Schedule',
            value: 'day_of_month_dropdown',
          },
        ],
      },
      {
        element: 'searchSelect',
        props: {
          label: 'Select a day',
          name: 'day_selection',
          // min: 0,
          // max: 1,
          step: 0.01,
          // required: true
        },
        options: daySelectionOptions,
        dependency: [
          {
            inputName: 'Schedule',
            value: 'day_selection',
          },
        ],
      },
      {
        element: 'searchSelect',
        props: {
          label: 'Select a time',
          name: 'time_options',
          // min: 0,
          // max: 1,
          step: 0.01,
          // required: true
        },
        options: timeOptions,
        dependency: [
          {
            inputName: 'Schedule',
            value: 'time_options',
          },
        ],
      },
      {
        element: 'searchSelect',
        props: {
          label: 'Select a timezone',
          name: 'timezone_dropdown',
          // min: 0,
          // max: 1,
          step: 0.01,
          // required: true
        },
        options: timeZoneOptions,
        dependency: [
          {
            inputName: 'Schedule',
            value: 'timezone_dropdown',
          },
        ],
      },
      {
        element: 'toggle',
        props: {
          label: 'Run on weekends (Sat, Sun)',
          name: 'toggle_weekends',
          // min: 0,
          // max: 1,
          step: 0.01,
          // required: true
        },
        options: timeZoneOptions,
        dependency: [
          {
            inputName: 'Schedule',
            value: 'toggle_weekends',
          },
        ],
      },
    ],
    test: '',
  },
  'Webhook API': {
    setup: [
      ...baseSetupConfig,
      {
        element: 'searchSelect',
        props: {
          label: 'Trigger Events',
          name: 'Webhook API', // name change with Webhook API API
          required: true,
        },
      },
    ],
    configuration: [
      {
        element: 'input',
        props: {
          label: 'Pick of a Child Key',
          name: 'child_key',
          editable: true,
          profileInputStyle: true,
          min: 0,
          isRequired: false,
        },
      },
    ],
    test: '',
  },
  Assistants: {
    setup: [
      ...baseSetupConfig,
      {
        element: 'searchSelect',
        props: {
          label: 'Assistants',
          name: 'Assistants',
          required: true,
        },
      },
    ],
    configuration: [
      {
        element: 'keyValuePair',
        props: {
          isAddRemoveDynamicOptions: false,
          name: 'dynamic_variables',
          withKeyLabels: true,
          isDynamicOptionsModel: true,
          isWithMessage: true,
        },
      },
    ],
    test: '',
  },
  'Send Prompt': {
    setup: [
      ...baseSetupConfig,
      {
        element: 'select',
        props: {
          label: 'Providers',
          name: 'provider_id',
          required: true,
        },
      },
      {
        element: 'select',
        props: {
          label: 'Models',
          name: 'model_id',
          required: true,
        },
      },
    ],
    configuration: [
      {
        element: 'slider',
        props: {
          label: 'temperature',
          name: 'temperature',
          valueLabelDisplay: 'auto',
          min: 0,
          max: 1,
          step: 0.01,
          required: true,
        },
      },
      {
        element: 'input',
        props: {
          label: 'Maximum tokens',
          name: 'max_tokens',
          min: 1,
          type: 'number',
          placeholder: 'Enter tokens',
          isRequired: true,
          required: true,
        },
      },
      {
        element: 'textarea',
        props: {
          label: 'Prompt',
          name: 'human_prompt',
          rows: 2,
          required: true,
        },
      },
    ],
    test: '',
  },
  API: {
    setup: [
      ...baseSetupConfig,
      {
        element: 'select',
        props: {
          label: 'Select Method',
          name: 'method',
          required: true,
        },
        options: methods,
      },
    ],
    configuration: [
      {
        element: 'input',
        props: {
          label: 'URL',
          name: 'url',
          type: 'text',
          placeholder: 'Enter URL',
          isRequired: true,
          required: true,
        },
      },
      {
        element: 'keyValuePair',
        props: {
          label: 'Headers',
          name: 'headers',
          isAddRemoveDynamicOptions: true,
        },
        keyValuePairs: [{ key: '', value: '' }],
      },
      {
        element: 'keyValuePair',
        props: {
          label: 'Query Params',
          name: 'query_params',
          isAddRemoveDynamicOptions: true,
        },
        keyValuePairs: [{ key: '', value: '' }],
      },
      {
        element: 'textarea',
        props: {
          label: 'Raw',
          name: 'raw',
          rows: 2,
          placeholder: 'Body Text JSON',
          required: true,
        },
        dependency: [
          {
            inputName: 'body_type',
            value: 'JSON',
          },
        ],
      },
      {
        element: 'keyValuePair',
        props: {
          label: 'Body',
          name: 'body',
          isAddRemoveDynamicOptions: true,
        },
        keyValuePairs: [{ key: '', value: '' }],
        dependency: [
          {
            inputName: 'body_type',
            value: 'Form',
          },
        ],
      },
    ],
    test: '',
  },
  Gateways: {
    setup: [
      ...baseSetupConfig,
      {
        element: 'searchSelect',
        props: {
          label: 'Gateways',
          name: 'Gateways',
          required: true,
        },
      },
    ],
    configuration: [
      {
        element: 'textarea',
        props: {
          label: 'JSON',
          name: 'gateway_config',
          rows: 2,
          placeholder: 'Body Text JSON',
          required: true,
        },
      },
    ],
    test: '',
  },
  'Send HTTP Request': {
    configuration: [],
    setup: [],
    test: '',
  },
  'send prompt': {
    configuration: [],
    setup: [],
    test: '',
  },
};

// making alias
WorkFlowData = {
  ...WorkFlowData,
  'Send HTTP Request': { ...WorkFlowData.API },
  'send prompt': { ...WorkFlowData['Send Prompt'] },
};

export const WorkFlowPanelData = WorkFlowData;
